import { observer } from 'mobx-react';
import { FC, useEffect, useMemo } from 'react';
import { Route } from 'react-router-dom';
import { OrderConditions } from './screens/OrderConditions/OrderConditions';
import { OrderNotes } from './screens/OrderNotes/OrderNotes';
import { orderConfigurationStore } from './store/orderConfigurationStore';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import { history } from 'src/services/history';
import { LoadingStatus } from 'src/types/main';
import { leaveWithoutSaving } from 'src/utils/modalConfirmations';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import { Table } from 'src/view/components/Table/Table';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import ScreenTabsNavigation from 'src/view/containers/ScreenTabsNavigation/ScreenTabsNavigation';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { BUTTON_TITLE } from '~/constants/main';
import { scrollToErrorElement } from '~/utils/helpers';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { appStore } from '~/mobx/appStore';

const OrderConfiguration: FC = observer((): JSX.Element => {
  useEffect(() => {
    orderConfigurationStore.fetchOrderSettings();
    return () => orderConfigurationStore.reset();
  }, []);

  const handleBackClick = () => {
    if (orderConfigurationStore.isDataChanged) {
      leaveWithoutSaving({ onConfirm: () => history.push(SCREENS.SETTINGS()) });
    } else {
      history.push(SCREENS.SETTINGS());
    }
  };

  const handleSave = () => {
    orderConfigurationStore.validateBeforeSave();

    if (orderConfigurationStore.isValidationFailed) {
      scrollToErrorElement();
    } else {
      orderConfigurationStore.updateOrderSettings();
    }
  };

  const navigationTabsOptions = useMemo(() => {
    if (appStore.isAddonsAvailable.franchise) return [];

    return [
      {
        path: SCREENS.SETTINGS_ORDERS_CONDITIONS(),
        title: 'conditions',
        className: orderConfigurationStore.isValidationFailed ? 'border-red' : '',
      },
      { path: SCREENS.SETTINGS_ORDERS_NOTES(), title: 'Order_notes' },
    ];
  }, [orderConfigurationStore.isValidationFailed, appStore.isAddonsAvailable.franchise]);

  return (
    <div className="d-flex flex-column full-width">
      <Headers>
        <HeaderBack title={localeStore.t('Order_configuration')} path={SCREENS.SETTINGS()} callback={handleBackClick} />
      </Headers>
      <div className="d-flex flex-column flex-grow-1">
        <Table>
          <ScreenTabsNavigation options={navigationTabsOptions} className="my-6" />
          <ScrollableBlock>
            <Route path={SCREENS.SETTINGS_ORDERS_CONDITIONS()} component={OrderConditions} />
            <Route path={SCREENS.SETTINGS_ORDERS_NOTES()} component={OrderNotes} />
            <Button title={localeStore.t(BUTTON_TITLE.SAVE)} className="mt-8" onClick={handleSave} />
          </ScrollableBlock>
        </Table>
      </div>

      <LoadingOverlay
        visible={[LoadingStatus.Loading, LoadingStatus.Refreshing].includes(orderConfigurationStore.settingsLoadingStatus)}
        refreshing
      />
    </div>
  );
});

export default OrderConfiguration;
